/**
 * ==========================================================================
 *  The things in the level that open: the doors and their painikkeet, the
 *  veräjä at the top of the palju's yard, and the pane in the pirtti's south
 *  window.
 *
 *  All three are the same split, and it is the important thing about this
 *  file. The *simulation* owns whether a door is open, how far the gate has
 *  swung and whether the glass is broken - `src/world/Door.ts`, `Kamiina` and
 *  `Ikkuna` do, and the boxes a runner is pushed out of are cut from those
 *  same numbers. Nothing here decides anything; it reads an angle or a flag
 *  back out and turns a mesh by it, so what you see and what shoves you
 *  cannot come apart.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { DOOR_HANDLE } from '../config/gameplay';
import { sampleShard, shardPose, shardSeeds, type ShardSeed } from './Shatter';
import type { SceneAssets } from './SceneAssets';
import { smoothStep } from '../utils/math';
import type { Door } from '../world/Door';
import type { PaneImpact } from '../world/Ikkuna';
import { boxCenter, boxSize, handle, type HandleSpec, type LevelDefinition } from '../world/Level';
import { PALETTE } from './surfaces';
import { mulberry32 } from '../utils/prng';

/** How far the veräjä's leaf swings. A farm gate opens wide or it is in the way. */
const KLAPI_SWING = Math.PI * 0.62;

/**
 * How solid a pane of glass is. Low enough to see the takapiha through - which
 * is the whole point of glazing the window rather than boarding it - and high
 * enough that the sheet catches the sky and reads as *there*. A window you
 * cannot see is a window nobody knows they broke.
 */
const GLASS_OPACITY = 0.26;

/** One shard of the pirtti's window, plus which piece of the pane it is. */
interface Shard {
  mesh: THREE.Mesh;
  seed: ShardSeed;
}

export class Openings {
  /**
   * The four turned pieces a painike is made of, shared by every door in the
   * level: there are fifteen handles in it and one of each of these. Left
   * standing along +Y the way `CylinderGeometry` builds them, and turned onto
   * the axis they belong on by the meshes - see `handle`.
   */
  private readonly handleParts = {
    rose: new THREE.CylinderGeometry(
      DOOR_HANDLE.roseRadius,
      DOOR_HANDLE.roseRadius,
      DOOR_HANDLE.roseDepth + DOOR_HANDLE.sink,
      12,
    ),
    neck: new THREE.CylinderGeometry(
      DOOR_HANDLE.neckRadius,
      DOOR_HANDLE.neckRadius,
      DOOR_HANDLE.roseDepth + DOOR_HANDLE.neckLength,
      8,
    ),
    // Thinner at the end you take hold of than at the neck it grows out of,
    // which is what makes a lever read as cast rather than as a length of bar.
    lever: new THREE.CylinderGeometry(
      DOOR_HANDLE.tipRadius,
      DOOR_HANDLE.leverRadius,
      DOOR_HANDLE.leverLength,
      8,
    ),
    tip: new THREE.CylinderGeometry(
      DOOR_HANDLE.tipRadius,
      DOOR_HANDLE.tipRadius,
      DOOR_HANDLE.tipLength,
      8,
    ),
  };

  /** Door id -> the swinging leaf. The Door objects themselves live in the sim. */
  private readonly doorGroups = new Map<string, THREE.Group>();
  private verajaGroup: THREE.Group | null = null;
  /** The pane in the pirtti's south window, and the pieces it comes apart into. */
  private paneMesh: THREE.Mesh | null = null;
  private readonly shards: Shard[] = [];
  private readonly shardScratch = shardPose();

  constructor(
    private readonly assets: SceneAssets,
    private readonly level: LevelDefinition,
  ) {}

  /** Everything that opens, for the caller to put in the scene. */
  build(): THREE.Object3D[] {
    const out: THREE.Object3D[] = [];
    this.buildDoors(out);
    this.buildHandles(out);
    this.buildVeraja(out);
    this.buildIkkuna(out);
    return out;
  }

  /**
   * Render-rate update: purely visual, and not one angle of its own.
   *
   * `Door.angle` is the simulation's own answer to where the leaf is pointed -
   * ease and all - and the boxes a runner is pushed out of are cut from that
   * same number. So the leaf you can see and the leaf that shoves you cannot
   * come apart, which matters a great deal more now that they are the same
   * thing: see `src/world/Door.ts`.
   */
  renderDoors(doors: readonly Door[]): void {
    for (const door of doors) {
      const group = this.doorGroups.get(door.id);
      if (group) group.rotation.y = door.angle;
    }
  }

    private buildDoors(out: THREE.Object3D[]): void {
      for (const spec of this.level.doors) {
        const size = boxSize(spec.box);
        const center = boxCenter(spec.box);

        const group = new THREE.Group();
        group.position.set(spec.hinge[0], 0, spec.hinge[1]);

        const leaf = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('door'));
        leaf.scale.set(size.x, size.y, size.z);
        leaf.position.set(center.x - spec.hinge[0], center.y, center.z - spec.hinge[1]);
        group.add(leaf);

        // A painike on each face, because a door you open has both of them in a
        // room: the one you pull and the one that goes past you. Which end of
        // the leaf the hinge is on is not authored twice - it is read back off
        // the hinge the door already swings about.
        const alongZ = size.x < size.z;
        const hingeEnd = (alongZ ? spec.hinge[1] - center.z : spec.hinge[0] - center.x) > 0 ? 1 : -1;
        for (const face of [1, -1]) {
          const painike = handle(`${spec.id}-painike`, spec.box, hingeEnd, face);
          // Parented to the leaf's own group and placed off the hinge it turns
          // about, so the handle swings with the door rather than hanging in the
          // doorway it came out of.
          group.add(this.handle({
            ...painike,
            at: {
              x: painike.at.x - spec.hinge[0],
              y: painike.at.y,
              z: painike.at.z - spec.hinge[1],
            },
          }));
        }

        out.push(group);
        this.doorGroups.set(spec.id, group);
      }
    }

    /** The painikkeet on the leaves that never open. See `HandleSpec`. */
    private buildHandles(out: THREE.Object3D[]): void {
      for (const spec of this.level.handles) out.push(this.handle(spec));
    }

    /**
     * One painike, in the frame `HandleSpec` describes: local +Z out of the face
     * it is screwed to, local +X the way `reach` says the lever goes.
     *
     * Four turned pieces - the rose on the leaf, the neck out of it, the lever
     * and the tip it is turned back at - and every measurement of them is in
     * `DOOR_HANDLE`, because every door in the level wears this same handle.
     * Nothing here is ever stepped: a handle is drawn once at load and then only
     * ever moved by the door it is parented to.
     */
    handle(spec: HandleSpec): THREE.Group {
      const steel = this.assets.material('metal');
      const standoff = DOOR_HANDLE.roseDepth + DOOR_HANDLE.neckLength;

      const group = new THREE.Group();
      group.name = spec.name;
      group.position.set(spec.at.x, spec.at.y, spec.at.z);
      group.rotation.y = spec.rotationY;

      // The rose, standing off the leaf and sunk a few millimetres into it - see
      // `DOOR_HANDLE.sink` for what that buys.
      const rose = new THREE.Mesh(this.handleParts.rose, steel);
      rose.rotation.x = Math.PI / 2;
      rose.position.z = (DOOR_HANDLE.roseDepth - DOOR_HANDLE.sink) / 2;
      group.add(rose);

      // The neck, out of the middle of it as far as the lever's centre line. It
      // starts at the face rather than at the top of the rose, so its own base
      // is buried inside the rose instead of sharing a plane with it.
      const neck = new THREE.Mesh(this.handleParts.neck, steel);
      neck.rotation.x = Math.PI / 2;
      neck.position.z = standoff / 2;
      group.add(neck);

      // And the lever, reaching back across the leaf towards the hinge.
      const lever = new THREE.Mesh(this.handleParts.lever, steel);
      lever.rotation.z = (-spec.reach * Math.PI) / 2;
      lever.position.set((spec.reach * DOOR_HANDLE.leverLength) / 2, 0, standoff);
      group.add(lever);

      // Its free end, turned back towards the door. Two centimetres of it, which
      // is the difference between a handle and a peg.
      const tip = new THREE.Mesh(this.handleParts.tip, steel);
      tip.rotation.x = Math.PI / 2;
      tip.position.set(
        spec.reach * DOOR_HANDLE.leverLength,
        0,
        standoff - DOOR_HANDLE.tipLength / 2,
      );
      group.add(tip);
      return group;
    }

    /**
     * The veräjä at the top of the palju's yard: one leaf, hinged on the south
     * pieli, swinging out into the navetta's front yard.
     *
     * The same split as a door - `Kamiina` owns the collider and the progress,
     * and this only ever reads the number back out.
     */
    private buildVeraja(out: THREE.Object3D[]): void {
      const spec = this.level.kamiina;
      const gate = this.level.solids.find((item) => item.name === spec.gate);
      if (!gate) return;

      const size = boxSize(gate.box);
      const center = boxCenter(gate.box);
      const group = new THREE.Group();
      group.position.set(spec.gateHinge[0], 0, spec.gateHinge[1]);

      // A farm gate is rails and two stiles, not a slab. Drawn as three rails so
      // the yard is still visible through it - a solid leaf across the only way
      // out would read as the edge of the level rather than as a shut gate.
      const long = Math.max(size.x, size.z);
      const alongZ = size.z > size.x;
      for (const [heightFraction, thickness] of [
        [0.15, 0.09],
        [0.5, 0.09],
        [0.85, 0.09],
      ] as [number, number][]) {
        const rail = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('wood'));
        rail.scale.set(alongZ ? size.x : long, thickness, alongZ ? long : size.z);
        rail.position.set(center.x - spec.gateHinge[0], size.y * heightFraction, center.z - spec.gateHinge[1]);
        group.add(rail);
      }
      for (const end of [0.06, 0.94]) {
        const stile = new THREE.Mesh(this.assets.boxGeometry, this.assets.material('wood'));
        stile.scale.set(alongZ ? size.x : 0.1, size.y, alongZ ? 0.1 : size.z);
        const along = (end - 0.5) * long;
        stile.position.set(
          center.x - spec.gateHinge[0] + (alongZ ? 0 : along),
          size.y / 2,
          center.z - spec.gateHinge[1] + (alongZ ? along : 0),
        );
        group.add(stile);
      }

      this.verajaGroup = group;
      out.push(group);
    }

    /** Swings the leaf. `progress` is `Kamiina.gateOpenProgress`. */
    renderVeraja(progress: number): void {
      if (!this.verajaGroup) return;
      this.verajaGroup.rotation.y =
        this.level.kamiina.gateSwing * KLAPI_SWING * smoothStep(progress);
    }

    /**
     * The glass in Einon pirtti's south window: one sheet, and the ninety-six
     * pieces it turns into.
     *
     * Unlit, like the monitors and the valkokangas and for the same reason
     * turned round: a monitor is a light source rather than a lit surface, and a
     * pane of glass is a *reflection* rather than a lit surface. Almost nothing
     * you see in a window at eight in the morning came off the glass diffusely -
     * it is the sky, and on this level the sky is one flat cold blue. Lambert
     * shading would light the sheet like a painted board and it would read as
     * one, which is the single thing this must not do: the runner has to be able
     * to see the takapiha through it to aim the dive at all.
     *
     * The sheet and the shards are built once and never rebuilt. Breaking the
     * window hides one mesh and shows ninety-six; restarting the run does the
     * reverse. Nothing is allocated on the frame the glass goes.
     */
    private buildIkkuna(out: THREE.Object3D[]): void {
      const spec = this.level.ikkuna;
      const pane = spec.pane;
      const size = boxSize(pane);
      const centre = boxCenter(pane);

      const glass = (): THREE.Material =>
        new THREE.MeshBasicMaterial({
          color: PALETTE.windowGlass,
          transparent: true,
          opacity: GLASS_OPACITY,
          // Glass that writes depth cuts a hole in whatever is behind it - which
          // here is the entire takapiha, i.e. everything the dive is aimed at.
          depthWrite: false,
          side: THREE.DoubleSide,
        });

      const sheet = new THREE.Mesh(this.assets.boxGeometry, glass());
      sheet.name = 'pirtin-ikkunalasi';
      sheet.scale.set(size.x, size.y, size.z);
      sheet.position.set(centre.x, centre.y, centre.z);
      this.paneMesh = sheet;
      out.push(sheet);

      // Authoring randomness, once, at load - the same allowance `prng.ts` is
      // written for, and the reason two players watching one replay watch the
      // same window break. Its own seed, so re-cutting the pane cannot shuffle
      // the smoke in the yard.
      const seeds = shardSeeds(mulberry32(0x9a55ed));
      // A shard is a chip of glass, not a slab: drawn as a plane so it is thin
      // from every angle, and double-sided so a tumbling one never blinks out.
      const chip = new THREE.PlaneGeometry(1, 1);
      for (const seed of seeds) {
        const mesh = new THREE.Mesh(chip, glass());
        mesh.name = `pirtin-ikkunasiru-${seed.column}-${seed.row}`;
        mesh.visible = false;
        this.shards.push({ mesh, seed });
        out.push(mesh);
      }
    }

    /**
     * The window, at whatever it is doing this frame.
     *
     * `brokenFor` is seconds since the glass went, or null while it is still in
     * the frame - the same shape `renderFire` takes, and read back out of the
     * simulation the same way. Rendering never decides that the glass broke; it
     * is told, and it is told in simulation seconds so that two machines playing
     * the same replay break the window on the same step.
     */
    renderIkkuna(brokenFor: number | null, impact: PaneImpact): void {
      if (this.paneMesh) this.paneMesh.visible = brokenFor === null;
      if (brokenFor === null) {
        for (const shard of this.shards) shard.mesh.visible = false;
        return;
      }

      const spec = this.level.ikkuna;
      for (const shard of this.shards) {
        const pose = sampleShard(spec, shard.seed, impact, brokenFor, this.shardScratch);
        // Everything is down and gone. Nothing to draw, and nothing to compute
        // for the rest of the run - which is most of the run.
        if (pose.opacity <= 0) {
          shard.mesh.visible = false;
          continue;
        }
        shard.mesh.visible = true;
        shard.mesh.position.set(pose.x, pose.y, pose.z);
        shard.mesh.scale.set(pose.width, pose.height, 1);
        shard.mesh.rotation.set(pose.rotationX, pose.rotationY, pose.rotationZ);
        (shard.mesh.material as THREE.MeshBasicMaterial).opacity = pose.opacity * GLASS_OPACITY;
      }
    }
}
